import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, ArrowLeft, Loader2, ShieldCheck, TrendingUp, Sparkles, Download } from "lucide-react";
import { runAssessment } from "@/lib/api";
import { toast } from "sonner";
import { downloadAssessmentReport } from "@/components/AssessmentReport";

const STEPS = ["Profile", "Income & Savings", "Existing Cover", "Goals", "You"];

const GOALS = [
  { id: "retirement", label: "Retirement" },
  { id: "child_education", label: "Child Education" },
  { id: "wealth", label: "Wealth Creation" },
  { id: "home", label: "Home Purchase" },
  { id: "tax", label: "Tax Saving" },
];

export default function NeedsAssessment() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    age: 32,
    married: false,
    dependents: 0,
    business_owner: false,
    annual_income: 1200000,
    monthly_savings: 25000,
    has_health_cover: false,
    has_term_cover: false,
    smoker: false,
    goals: [],
    name: "",
    phone: "",
    email: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadAssessmentReport(data, result);
      toast.success("Report downloaded. Check your downloads folder.");
    } catch (e) {
      toast.error("Could not generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleGoal = (g) =>
    setData((d) => ({ ...d, goals: d.goals.includes(g) ? d.goals.filter((x) => x !== g) : [...d.goals, g] }));

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!data.name || !data.phone) {
      toast.error("Please share your name and phone to receive recommendations.");
      return;
    }
    setLoading(true);
    try {
      const res = await runAssessment(data);
      setResult(res);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const priorityRank = { high: 0, medium: 1, low: 2 };
    const sorted = [...result.recommendations].sort((a, b) => priorityRank[a.priority] - priorityRank[b.priority]);
    return (
      <div className="bg-white pt-24 sm:pt-28" data-testid="assessment-result">
        <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50/60"><Sparkles className="h-3 w-3 mr-1" /> Personalised Report</Badge>
          <h1 className="font-display mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
            Your Financial Health Score
          </h1>

          <div className="mt-10 grid lg:grid-cols-3 gap-6">
            <Card className="p-8 lg:col-span-1 soft-shadow border-slate-100">
              <div className="flex flex-col items-center text-center">
                <div className="relative h-44 w-44">
                  <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="44" stroke="#E5E7EB" strokeWidth="8" fill="none" />
                    <circle cx="50" cy="50" r="44" stroke="#10B981" strokeWidth="8" fill="none"
                      strokeDasharray={`${(2 * Math.PI * 44 * result.health_score) / 100} ${2 * Math.PI * 44}`}
                      strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-display text-5xl font-semibold text-slate-900">{result.health_score}</span>
                    <span className="text-xs text-slate-500 mt-1">out of 100</span>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mt-6 leading-relaxed">
                  {result.health_score >= 75 ? "Excellent foundation. A few fine-tunes will optimise it." :
                   result.health_score >= 50 ? "Decent start — but meaningful gaps remain." :
                   "Several urgent gaps. Let's prioritise the high-impact ones."}
                </p>
              </div>
            </Card>

            <Card className="p-8 lg:col-span-2 soft-shadow border-slate-100">
              <h2 className="font-display text-xl font-semibold text-slate-900">Your priority recommendations</h2>
              <p className="text-sm text-slate-500 mt-1">{sorted.length} items, ranked by urgency.</p>
              <div className="mt-6 space-y-4">
                {sorted.map((r, i) => (
                  <div key={i} data-testid={`rec-${i}`} className="rounded-xl border border-slate-100 p-5 bg-slate-50/40">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="font-display text-base font-semibold text-slate-900">{r.product}</div>
                      <Badge className={
                        r.priority === "high" ? "bg-rose-100 text-rose-700 border-rose-200" :
                        r.priority === "medium" ? "bg-amber-100 text-amber-700 border-amber-200" :
                        "bg-slate-100 text-slate-600 border-slate-200"
                      }>{r.priority.toUpperCase()}</Badge>
                    </div>
                    {r.suggested_cover && (
                      <div className="mt-1.5 text-sm text-emerald-700 font-medium">{r.suggested_cover}</div>
                    )}
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.rationale}</p>
                  </div>
                ))}
              </div>
              <Button data-testid="assessment-cta-advisor" className="mt-8 bg-blue-900 hover:bg-blue-950 text-white" onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })}>
                Speak to an advisor about this <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <Button data-testid="assessment-cta-download" onClick={handleDownload} disabled={downloading} variant="outline" className="mt-8 ml-3 border-emerald-600 text-emerald-700 hover:bg-emerald-50">
                {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
                Download PDF report
              </Button>
            </Card>
          </div>

          <Card className="mt-10 p-8 gradient-blue text-white border-0">
            <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
              <div>
                <h3 className="font-display text-xl font-semibold">Want a written, detailed plan?</h3>
                <p className="text-white/85 text-sm mt-1">A senior advisor will share a personalised PDF & call you within 2 hours.</p>
              </div>
              <a href={`https://wa.me/919821237924?text=Hi%2C+I+just+completed+the+needs+assessment.+Health+score+${result.health_score}.+Please+share+a+detailed+plan.`} target="_blank" rel="noreferrer">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="assessment-cta-whatsapp">
                  WhatsApp my advisor
                </Button>
              </a>
            </div>
          </Card>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-white pt-24 sm:pt-28" data-testid="page-assessment">
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12">
        <Badge variant="outline" className="border-blue-200 text-blue-900 bg-blue-50/60">
          <Sparkles className="h-3 w-3 mr-1" /> Free • 3 minutes
        </Badge>
        <h1 className="font-display mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
          Insurance Needs Assessment
        </h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Eight quick questions. Personalised score & recommendations instantly.
        </p>

        <div className="mt-8 flex items-center gap-4">
          <Progress value={((step + 1) / STEPS.length) * 100} className="h-2" data-testid="assessment-progress" />
          <span className="text-xs text-slate-500 whitespace-nowrap">Step {step + 1} / {STEPS.length}</span>
        </div>

        <Card className="mt-8 p-6 sm:p-8 soft-shadow border-slate-100">
          {step === 0 && (
            <div className="space-y-6" data-testid="assessment-step-0">
              <div>
                <Label>Your age: <span className="font-semibold text-blue-900">{data.age}</span></Label>
                <Slider data-testid="assessment-slider-age" value={[data.age]} min={18} max={75} step={1} onValueChange={(v) => set("age", v[0])} className="mt-3" />
              </div>
              <div>
                <Label>Marital status</Label>
                <RadioGroup value={data.married ? "y" : "n"} onValueChange={(v) => set("married", v === "y")} className="mt-2 grid grid-cols-2 gap-3">
                  <Label className="border border-slate-200 rounded-lg p-3 cursor-pointer flex items-center gap-2 hover:border-slate-400" htmlFor="ms-n">
                    <RadioGroupItem value="n" id="ms-n" /> Single
                  </Label>
                  <Label className="border border-slate-200 rounded-lg p-3 cursor-pointer flex items-center gap-2 hover:border-slate-400" htmlFor="ms-y">
                    <RadioGroupItem value="y" id="ms-y" /> Married
                  </Label>
                </RadioGroup>
              </div>
              <div>
                <Label>Number of dependents: <span className="font-semibold text-blue-900">{data.dependents}</span></Label>
                <Slider data-testid="assessment-slider-dependents" value={[data.dependents]} min={0} max={6} step={1} onValueChange={(v) => set("dependents", v[0])} className="mt-3" />
              </div>
              <div className="flex items-center gap-3">
                <Checkbox id="biz" data-testid="assessment-check-business" checked={data.business_owner} onCheckedChange={(v) => set("business_owner", !!v)} />
                <Label htmlFor="biz" className="cursor-pointer">I run a business / professional practice</Label>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6" data-testid="assessment-step-1">
              <div>
                <Label>Annual income (₹): <span className="font-semibold text-blue-900">₹{(data.annual_income / 100000).toFixed(1)} L</span></Label>
                <Slider data-testid="assessment-slider-income" value={[data.annual_income]} min={300000} max={20000000} step={100000} onValueChange={(v) => set("annual_income", v[0])} className="mt-3" />
              </div>
              <div>
                <Label>Monthly savings & SIPs (₹): <span className="font-semibold text-blue-900">₹{data.monthly_savings.toLocaleString("en-IN")}</span></Label>
                <Slider data-testid="assessment-slider-savings" value={[data.monthly_savings]} min={0} max={300000} step={1000} onValueChange={(v) => set("monthly_savings", v[0])} className="mt-3" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="assessment-step-2">
              <Label className="flex items-center gap-3 border border-slate-200 rounded-lg p-4 cursor-pointer hover:border-slate-400">
                <Checkbox data-testid="assessment-check-health" checked={data.has_health_cover} onCheckedChange={(v) => set("has_health_cover", !!v)} />
                <div>
                  <div className="font-medium text-slate-900">I already have Health Insurance</div>
                  <div className="text-xs text-slate-500 mt-0.5">Adequate cover for self &amp; family</div>
                </div>
              </Label>
              <Label className="flex items-center gap-3 border border-slate-200 rounded-lg p-4 cursor-pointer hover:border-slate-400">
                <Checkbox data-testid="assessment-check-term" checked={data.has_term_cover} onCheckedChange={(v) => set("has_term_cover", !!v)} />
                <div>
                  <div className="font-medium text-slate-900">I already have Term Insurance</div>
                  <div className="text-xs text-slate-500 mt-0.5">Sum assured ≥ 10x annual income</div>
                </div>
              </Label>
              <Label className="flex items-center gap-3 border border-slate-200 rounded-lg p-4 cursor-pointer hover:border-slate-400">
                <Checkbox data-testid="assessment-check-smoker" checked={data.smoker} onCheckedChange={(v) => set("smoker", !!v)} />
                <div>
                  <div className="font-medium text-slate-900">I am a smoker / use tobacco</div>
                  <div className="text-xs text-slate-500 mt-0.5">Used in critical-illness recommendation</div>
                </div>
              </Label>
            </div>
          )}

          {step === 3 && (
            <div data-testid="assessment-step-3">
              <Label>Which of these matter to you?</Label>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {GOALS.map((g) => {
                  const active = data.goals.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      data-testid={`assessment-goal-${g.id}`}
                      onClick={() => toggleGoal(g.id)}
                      className={`text-left rounded-xl border p-4 transition ${
                        active ? "border-blue-900 bg-blue-50/60" : "border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      <div className={`text-sm font-medium ${active ? "text-blue-900" : "text-slate-900"}`}>{g.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5" data-testid="assessment-step-4">
              <p className="text-sm text-slate-600">Share your details to receive your personalised score & recommendations.</p>
              <div>
                <Label>Full name<span className="text-rose-500">*</span></Label>
                <Input data-testid="assessment-input-name" value={data.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Phone<span className="text-rose-500">*</span></Label>
                  <Input data-testid="assessment-input-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input data-testid="assessment-input-email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" />
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-3 border-t border-slate-100 pt-6">
            <Button variant="ghost" onClick={prev} disabled={step === 0} data-testid="assessment-prev">
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={next} className="bg-blue-900 hover:bg-blue-950 text-white" data-testid="assessment-next">
                Continue <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="assessment-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>See my report <ShieldCheck className="ml-1 h-4 w-4" /></>}
              </Button>
            )}
          </div>
        </Card>

        <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
          <TrendingUp className="h-3.5 w-3.5" />
          <span>Used by 2,400+ Indians to clarify their cover & investment needs.</span>
        </div>
      </section>
    </div>
  );
}
